import React from 'react'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'

export type AutoCoderImportState = 'preparing' | 'processing' | 'completed' | 'imported' | 'failed' | 'unknown'
export type AutoCoderRecordState = 'pending' | 'queued' | 'running' | 'completed' | 'failed'

interface AutoCoderStatusChipProps {
  state: string
  dataStatus?: string
  codingStatus?: string
  error?: string
  size?: 'small' | 'medium'
}

// Compute effective status - failures take precedence over displayed state
const getEffectiveStatus = (state: string, dataStatus?: string, codingStatus?: string): string => {
  if (dataStatus === 'failed' || codingStatus === 'failed') return 'failed'
  return state
}

const getStatusColor = (state: string): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
  switch (state) {
    // Import states
    case 'preparing':
      return 'default'
    case 'processing':
      return 'primary'
    case 'imported':
      return 'success'

    // The coding gate (long-lived pipelines lead with it —
    // see utils/pipelineDisplayStatus.ts)
    case 'coding on':
      return 'success'
    case 'coding off':
      return 'default'

    // Record states
    case 'pending':
      return 'default'
    case 'queued':
      return 'info'
    case 'running':
      return 'primary'

    // Shared states
    case 'completed':
      return 'success'
    case 'failed':
      return 'error'

    default:
      return 'default'
  }
}

const AutoCoderStatusChip: React.FC<AutoCoderStatusChipProps> = ({
  state,
  dataStatus,
  codingStatus,
  error,
  size = 'small',
}) => {
  const effectiveStatus = getEffectiveStatus(state, dataStatus, codingStatus)

  const chip = (
    <Chip
      label={effectiveStatus}
      color={getStatusColor(effectiveStatus)}
      size={size}
    />
  )

  if (error && effectiveStatus === 'failed') {
    return (
      <Tooltip title={error} arrow>
        {chip}
      </Tooltip>
    )
  }

  return chip
}

export default AutoCoderStatusChip
